import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowClockwise,
  ArrowRight,
  Buildings,
  CalendarCheck,
  ChartBar,
  CheckCircle,
  GraduationCap,
  IdentificationCard,
  Money,
  Student,
  WarningCircle,
} from "@phosphor-icons/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  {
    id: "overview",
    label: "Overview",
    labelKey: "organization.overview",
    icon: ChartBar,
  },
  {
    id: "branches",
    label: "Branches",
    labelKey: "organization.branches",
    icon: Buildings,
  },
  {
    id: "reports",
    label: "Reports",
    labelKey: "organization.reports",
    icon: IdentificationCard,
  },
];

const PIE_COLORS = ["#16A34A", "#F59E0B", "#E2E8F0"];

function money(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function pct(value) {
  return value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`;
}

function severityClass(severity) {
  if (severity === "critical") return "border-red-200 bg-red-50 text-red-700";
  if (severity === "warning") return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-yellow-200 bg-yellow-50 text-yellow-800";
}

function healthBadge(health, tr) {
  if (health === "critical") {
    return (
      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700">
        {tr("organization.health.critical", "Critical")}
      </span>
    );
  }
  if (health === "attention") {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800">
        {tr("organization.health.attention", "Needs Attention")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
      {tr("organization.health.good", "Good")}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, subtext }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 font-heading text-2xl font-extrabold text-slate-900">{value}</p>
          {subtext ? <p className="mt-1 text-xs text-slate-500">{subtext}</p> : null}
        </div>
        <div className="rounded-xl bg-slate-100 p-3 text-[#0F2747]">
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function OrganizationAdmin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchBranch } = useAuth();

  const tr = useCallback(
    (key, fallback, vars = {}) => t(key, { defaultValue: fallback, ...vars }),
    [t]
  );

  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openingBranch, setOpeningBranch] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, branchesRes] = await Promise.all([
        api.get("/organization/dashboard"),
        api.get("/organization/branches"),
      ]);
      setData(dashboardRes.data);
      setBranches(branchesRes.data || []);
    } catch (e) {
      toast.error(formatErr(e, tr("organization.loadFailed", "Unable to load organization dashboard")));
    } finally {
      setLoading(false);
    }
  }, [tr]);

  useEffect(() => {
    load();
  }, [load]);

  const openBranch = async (schoolId) => {
    if (!schoolId) return;
    try {
      setOpeningBranch(schoolId);
      await switchBranch(schoolId);
      navigate("/school");
    } catch (e) {
      toast.error(formatErr(e, tr("organization.openBranchFailed", "Unable to open branch dashboard")));
    } finally {
      setOpeningBranch("");
    }
  };

  const branchSummaries = useMemo(() => data?.branches || [], [data]);

  const displayedBranches = useMemo(() => {
    if (selectedBranch === "all") return branchSummaries;
    return branchSummaries.filter((branch) => branch.id === selectedBranch);
  }, [branchSummaries, selectedBranch]);

  const displayedIssues = useMemo(() => {
    const issues = data?.needs_attention || [];
    if (selectedBranch === "all") return issues;
    return issues.filter((issue) => issue.branch_id === selectedBranch);
  }, [data, selectedBranch]);

  const feeChart = useMemo(
    () =>
      displayedBranches.map((branch) => ({
        name: branch.branch_name || branch.name || branch.branch_code,
        shortName: String(branch.branch_code || branch.branch_name || branch.name || "").slice(0, 14),
        collected: Number(branch.fees_collected || 0),
        outstanding: Number(branch.fees_outstanding || 0),
      })),
    [displayedBranches]
  );

  const totalCollected = displayedBranches.reduce(
    (sum, branch) => sum + Number(branch.fees_collected || 0),
    0
  );
  const totalOutstanding = displayedBranches.reduce(
    (sum, branch) => sum + Number(branch.fees_outstanding || 0),
    0
  );
  const totalFeeBase = totalCollected + totalOutstanding;
  const feePie = [
    {
      name: tr("organization.feesCollected", "Collected"),
      value: totalCollected,
    },
    {
      name: tr("organization.outstanding", "Outstanding"),
      value: totalOutstanding,
    },
  ];

  const attendanceChart = displayedBranches.map((branch) => ({
    name: branch.branch_name || branch.name || branch.branch_code,
        shortName: String(branch.branch_code || branch.branch_name || branch.name || "").slice(0, 14),
    attendance:
      branch.attendance_today === null || branch.attendance_today === undefined
        ? 0
        : Number(branch.attendance_today),
  }));

  const organizationName =
    data?.organization?.name || tr("organization.organizationDashboard", "Organization Dashboard");

  const selectedSummary =
    selectedBranch === "all"
      ? data?.summary
      : (() => {
          const branch = displayedBranches[0];
          if (!branch) return data?.summary;
          return {
            active_branches: 1,
            students: branch.students,
            teachers: branch.teachers,
            attendance_today: branch.attendance_today,
            fees_collected: branch.fees_collected,
            fees_outstanding: branch.fees_outstanding,
            issue_count: branch.issue_count,
          };
        })();

  return (
    <DashboardShell
      brand={tr("organization.organizationAdmin", "Organization Admin")}
      nav={NAV}
      active={tab}
      onNavigate={setTab}
    >
      <div className="min-h-full bg-[#F8FAFC]">
        <div className="mx-auto max-w-[1500px] space-y-6 p-1 sm:p-2">
          <div className="rounded-3xl bg-[#0F2747] px-5 py-5 text-white shadow-sm sm:px-7">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-200">
                  {tr("organization.organizationDashboard", "Organization Dashboard")}
                </p>
                <h1 className="mt-1 font-heading text-2xl font-extrabold sm:text-3xl">
                  {organizationName}
                </h1>
                <p className="mt-1 text-sm text-slate-300">
                  {tr(
                    "organization.dashboardSubtitle",
                    "Monitor branches, attendance, fees and operational health from one place."
                  )}
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-full border-white/20 bg-white text-slate-900 sm:min-w-[230px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {tr("organization.allBranches", "All Branches")}
                    </SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.branch_name || branch.name}
                        {branch.branch_code ? ` · ${branch.branch_code}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  onClick={load}
                  disabled={loading}
                  className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                >
                  <ArrowClockwise size={17} className={loading ? "mr-2 animate-spin" : "mr-2"} />
                  {tr("organization.refresh", "Refresh")}
                </Button>
              </div>
            </div>
          </div>

          {loading && !data ? (
            <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-slate-200 bg-white">
              <div className="text-center">
                <ArrowClockwise size={28} className="mx-auto animate-spin text-[#2563EB]" />
                <p className="mt-3 text-sm font-semibold text-slate-500">
                  {tr("organization.loadingDashboard", "Loading organization dashboard...")}
                </p>
              </div>
            </div>
          ) : (
            <>
              {tab === "overview" && (
                <>
                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-red-50 p-2.5 text-red-600">
                          <WarningCircle size={22} weight="fill" />
                        </div>
                        <div>
                          <h2 className="font-heading text-lg font-extrabold text-slate-900">
                            {tr("organization.needsAttention", "Needs Attention")}
                          </h2>
                          <p className="text-xs text-slate-500">
                            {tr(
                              "organization.needsAttentionDescription",
                              "Operational items that may need action today."
                            )}
                          </p>
                        </div>
                      </div>
                      <span className="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                        {displayedIssues.length} {tr("organization.items", "items")}
                      </span>
                    </div>

                    {displayedIssues.length === 0 ? (
                      <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
                        <CheckCircle size={22} weight="fill" />
                        <p className="text-sm font-semibold">
                          {tr(
                            "organization.noCriticalIssues",
                            "No critical issues detected today. All selected branches are operating normally."
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="grid gap-3 lg:grid-cols-2">
                        {displayedIssues.map((issue, index) => (
                          <div
                            key={`${issue.branch_id}-${issue.code}-${index}`}
                            className={`rounded-xl border p-4 ${severityClass(issue.severity)}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-extrabold uppercase tracking-wide opacity-70">
                                  {issue.branch_name}
                                  {issue.branch_code ? ` · ${issue.branch_code}` : ""}
                                </p>
                                <p className="mt-1 text-sm font-bold">
                                  {tr(
                                    `organization.issues.${issue.code}`,
                                    issue.code === "no_current_academic_year"
                                      ? "No current Academic Year"
                                      : issue.code === "students_missing_academic_year_enrollment"
                                        ? "{{count}} students missing current-year enrollment"
                                        : issue.code === "attendance_not_marked_today"
                                          ? "Attendance not marked today"
                                          : issue.code === "overdue_fees"
                                            ? "{{count}} overdue fee items · Outstanding {{amount}}"
                                            : issue.code,
                                    {
                                      count: issue.params?.count || 0,
                                      amount: money(issue.params?.outstanding || 0),
                                    }
                                  )}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={openingBranch === issue.branch_id}
                                onClick={() => openBranch(issue.branch_id)}
                                className="shrink-0 bg-white/80"
                              >
                                {tr("organization.open", "Open")}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <StatCard
                      icon={Buildings}
                      label={tr("organization.activeBranches", "Active Branches")}
                      value={selectedSummary?.active_branches ?? 0}
                    />
                    <StatCard
                      icon={Student}
                      label={tr("organization.students", "Students")}
                      value={selectedSummary?.students ?? 0}
                    />
                    <StatCard
                      icon={CalendarCheck}
                      label={tr("organization.attendanceToday", "Attendance Today")}
                      value={pct(selectedSummary?.attendance_today)}
                    />
                    <StatCard
                      icon={Money}
                      label={tr("organization.feesCollected", "Fees Collected")}
                      value={money(selectedSummary?.fees_collected)}
                    />
                    <StatCard
                      icon={WarningCircle}
                      label={tr("organization.outstanding", "Outstanding")}
                      value={money(selectedSummary?.fees_outstanding)}
                    />
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-4">
                      <h2 className="font-heading text-lg font-extrabold text-slate-900">
                        {tr("organization.branchPerformance", "Branch Performance")}
                      </h2>
                      <p className="text-xs text-slate-500">
                        {tr(
                          "organization.branchPerformanceDescription",
                          "Compare academic year, attendance, fees and issues branch by branch."
                        )}
                      </p>
                    </div>

                    <div className="hidden overflow-x-auto md:block">
                      <table className="w-full min-w-[900px] text-left text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-500">
                            <th className="px-3 py-3">{tr("organization.branch", "Branch")}</th>
                            <th className="px-3 py-3">{tr("organization.academicYear", "Academic Year")}</th>
                            <th className="px-3 py-3">{tr("organization.students", "Students")}</th>
                            <th className="px-3 py-3">{tr("organization.attendance", "Attendance")}</th>
                            <th className="px-3 py-3">{tr("organization.collected", "Collected")}</th>
                            <th className="px-3 py-3">{tr("organization.outstanding", "Outstanding")}</th>
                            <th className="px-3 py-3">{tr("organization.healthLabel", "Health")}</th>
                            <th className="px-3 py-3 text-right">{tr("organization.action", "Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedBranches.map((branch) => (
                            <tr key={branch.id} className="border-b border-slate-100 last:border-0">
                              <td className="px-3 py-4">
                                <div className="font-bold text-slate-900">
                                  {branch.branch_name || branch.name}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {branch.is_main_branch
                                    ? tr("organization.mainBranch", "Main Branch")
                                    : tr("organization.branch", "Branch")}
                                  {branch.branch_code ? ` · ${branch.branch_code}` : ""}
                                </div>
                              </td>
                              <td className="px-3 py-4 text-slate-600">
                                {branch.academic_year?.name || "—"}
                              </td>
                              <td className="px-3 py-4 font-semibold">{branch.students}</td>
                              <td className="px-3 py-4 font-semibold">{pct(branch.attendance_today)}</td>
                              <td className="px-3 py-4 font-semibold text-emerald-700">
                                {money(branch.fees_collected)}
                              </td>
                              <td className="px-3 py-4 font-semibold text-amber-700">
                                {money(branch.fees_outstanding)}
                              </td>
                              <td className="px-3 py-4">{healthBadge(branch.health, tr)}</td>
                              <td className="px-3 py-4 text-right">
                                <Button
                                  size="sm"
                                  disabled={openingBranch === branch.id}
                                  onClick={() => openBranch(branch.id)}
                                  className="bg-[#2563EB] text-white hover:bg-blue-700"
                                >
                                  {tr("organization.openDashboard", "Open Dashboard")}
                                  <ArrowRight size={15} className="ml-1.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="space-y-3 md:hidden">
                      {displayedBranches.map((branch) => (
                        <div key={branch.id} className="rounded-xl border border-slate-200 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="font-bold text-slate-900">
                                {branch.branch_name || branch.name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {branch.branch_code || ""}
                                {branch.academic_year?.name ? ` · ${branch.academic_year.name}` : ""}
                              </p>
                            </div>
                            {healthBadge(branch.health, tr)}
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-slate-500">
                                {tr("organization.students", "Students")}
                              </p>
                              <p className="font-bold">{branch.students}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">
                                {tr("organization.attendance", "Attendance")}
                              </p>
                              <p className="font-bold">{pct(branch.attendance_today)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">
                                {tr("organization.collected", "Collected")}
                              </p>
                              <p className="font-bold text-emerald-700">
                                {money(branch.fees_collected)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">
                                {tr("organization.outstanding", "Outstanding")}
                              </p>
                              <p className="font-bold text-amber-700">
                                {money(branch.fees_outstanding)}
                              </p>
                            </div>
                          </div>
                          <Button
                            className="mt-4 w-full bg-[#2563EB] text-white hover:bg-blue-700"
                            disabled={openingBranch === branch.id}
                            onClick={() => openBranch(branch.id)}
                          >
                            {tr("organization.openDashboard", "Open Dashboard")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="grid gap-4 xl:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h2 className="font-heading text-lg font-extrabold text-slate-900">
                        {tr("organization.feeHealth", "Fee Health")}
                      </h2>
                      <p className="mb-4 text-xs text-slate-500">
                        {tr(
                          "organization.feeHealthDescription",
                          "Collected versus outstanding fees across selected branches."
                        )}
                      </p>
                      <div className="grid gap-8 2xl:grid-cols-[1.5fr_1fr]">
                        <div className="h-[340px] min-w-0 overflow-hidden">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={feeChart}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="shortName" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={72} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip formatter={(value) => money(value)} />
                              <Legend verticalAlign="top" height={32} />
                              <Bar
                                dataKey="collected"
                                name={tr("organization.collected", "Collected")}
                                fill="#16A34A"
                                radius={[5, 5, 0, 0]}
                              />
                              <Bar
                                dataKey="outstanding"
                                name={tr("organization.outstanding", "Outstanding")}
                                fill="#F59E0B"
                                radius={[5, 5, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        <div className="h-[320px] min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={feePie}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={55}
                                outerRadius={85}
                                paddingAngle={3}
                              >
                                {feePie.map((entry, index) => (
                                  <Cell
                                    key={`${entry.name}-${index}`}
                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => money(value)} />
                              <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                          </ResponsiveContainer>
                          <p className="mt-1 text-center text-xs font-semibold text-slate-500">
                            {totalFeeBase > 0
                              ? `${Math.round((totalCollected * 100) / totalFeeBase)}% ${tr(
                                  "organization.collectionRate",
                                  "collection rate"
                                )}`
                              : tr("organization.noFeeData", "No fee data yet")}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h2 className="font-heading text-lg font-extrabold text-slate-900">
                        {tr("organization.attendanceToday", "Attendance Today")}
                      </h2>
                      <p className="mb-4 text-xs text-slate-500">
                        {tr(
                          "organization.attendanceDescription",
                          "Today's student attendance percentage by branch."
                        )}
                      </p>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={attendanceChart}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="shortName" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={72} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Bar
                              dataKey="attendance"
                              name={tr("organization.attendance", "Attendance")}
                              fill="#2563EB"
                              radius={[6, 6, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </section>

                  <section className="grid gap-4 lg:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-blue-50 p-3 text-blue-600">
                          <GraduationCap size={22} weight="duotone" />
                        </div>
                        <div>
                          <h2 className="font-heading font-extrabold text-slate-900">
                            {tr("organization.academicYearHealth", "Academic Year Health")}
                          </h2>
                          <p className="text-xs text-slate-500">
                            {tr(
                              "organization.academicYearHealthDescription",
                              "Current Academic Year configuration across branches."
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        {displayedBranches.map((branch) => (
                          <div
                            key={branch.id}
                            className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3"
                          >
                            <span className="text-sm font-semibold text-slate-700">
                              {branch.branch_name || branch.name}
                            </span>
                            <span
                              className={`text-xs font-bold ${
                                branch.academic_year ? "text-emerald-700" : "text-red-600"
                              }`}
                            >
                              {branch.academic_year?.name ||
                                tr("organization.notConfigured", "Not configured")}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-violet-50 p-3 text-violet-600">
                          <IdentificationCard size={22} weight="duotone" />
                        </div>
                        <div>
                          <h2 className="font-heading font-extrabold text-slate-900">
                            {tr("organization.staffOverview", "Staff Overview")}
                          </h2>
                          <p className="text-xs text-slate-500">
                            {tr(
                              "organization.staffOverviewDescription",
                              "Teacher strength and today's staff attendance."
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        {displayedBranches.map((branch) => (
                          <div
                            key={branch.id}
                            className="grid grid-cols-1 gap-1 rounded-xl bg-slate-50 px-4 py-3 sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-3"
                          >
                            <span className="truncate text-sm font-semibold text-slate-700">
                              {branch.branch_name || branch.name}
                            </span>
                            <span className="text-xs font-bold text-slate-600">
                              {branch.teachers} {tr("organization.teachers", "Teachers")}
                            </span>
                            <span className="text-xs font-bold text-blue-700">
                              {pct(branch.staff_attendance_today)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                </>
              )}

              {tab === "branches" && (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-5">
                    <h2 className="font-heading text-xl font-extrabold text-slate-900">
                      {tr("organization.manageBranches", "Manage Branches")}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {tr(
                        "organization.branchesDescription",
                        "View every branch in your organization and open its School Admin dashboard."
                      )}
                    </p>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    {branchSummaries.map((branch) => (
                      <div key={branch.id} className="rounded-2xl border border-slate-200 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <Buildings size={20} className="text-[#2563EB]" />
                              <h3 className="font-heading font-extrabold text-slate-900">
                                {branch.branch_name || branch.name}
                              </h3>
                            </div>
                            <p className="mt-1 text-xs text-slate-500">
                              {branch.is_main_branch
                                ? tr("organization.mainBranch", "Main Branch")
                                : tr("organization.branch", "Branch")}
                              {branch.branch_code ? ` · ${branch.branch_code}` : ""}
                            </p>
                          </div>
                          {healthBadge(branch.health, tr)}
                        </div>

                        <div className="mt-5 grid grid-cols-2 gap-3">
                          <div className="rounded-xl bg-slate-50 p-3">
                            <p className="text-xs text-slate-500">
                              {tr("organization.students", "Students")}
                            </p>
                            <p className="mt-1 font-heading text-xl font-extrabold">
                              {branch.students}
                            </p>
                          </div>
                          <div className="rounded-xl bg-slate-50 p-3">
                            <p className="text-xs text-slate-500">
                              {tr("organization.teachers", "Teachers")}
                            </p>
                            <p className="mt-1 font-heading text-xl font-extrabold">
                              {branch.teachers}
                            </p>
                          </div>
                          <div className="rounded-xl bg-slate-50 p-3">
                            <p className="text-xs text-slate-500">
                              {tr("organization.attendance", "Attendance")}
                            </p>
                            <p className="mt-1 font-heading text-xl font-extrabold">
                              {pct(branch.attendance_today)}
                            </p>
                          </div>
                          <div className="rounded-xl bg-slate-50 p-3">
                            <p className="text-xs text-slate-500">
                              {tr("organization.outstanding", "Outstanding")}
                            </p>
                            <p className="mt-1 font-heading text-lg font-extrabold text-amber-700">
                              {money(branch.fees_outstanding)}
                            </p>
                          </div>
                        </div>

                        <Button
                          className="mt-5 w-full bg-[#2563EB] text-white hover:bg-blue-700"
                          disabled={openingBranch === branch.id}
                          onClick={() => openBranch(branch.id)}
                        >
                          {tr("organization.openDashboard", "Open Dashboard")}
                          <ArrowRight size={16} className="ml-2" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {tab === "reports" && (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-5">
                    <h2 className="font-heading text-xl font-extrabold text-slate-900">
                      {tr("organization.reports", "Reports")}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {tr(
                        "organization.reportsDescription",
                        "Organization-level operational summary. Detailed exports can be opened from each branch dashboard."
                      )}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard
                      icon={Buildings}
                      label={tr("organization.activeBranches", "Active Branches")}
                      value={data?.summary?.active_branches ?? 0}
                    />
                    <StatCard
                      icon={Student}
                      label={tr("organization.students", "Students")}
                      value={data?.summary?.students ?? 0}
                    />
                    <StatCard
                      icon={GraduationCap}
                      label={tr("organization.teachers", "Teachers")}
                      value={data?.summary?.teachers ?? 0}
                    />
                    <StatCard
                      icon={WarningCircle}
                      label={tr("organization.needsAttention", "Needs Attention")}
                      value={data?.summary?.issue_count ?? 0}
                    />
                  </div>

                </section>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
